import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plan, Arrival, Leg, Step } from 'src/generated/brezavta/requests/types.gen'
import { listArrivalsStopsStopIdArrivalsGet } from 'src/generated/brezavta/requests'
import { notify, requestNotificationPermission, resetAllNotifications } from 'src/lib/navigatorNotifications'
import { TRANSIT_MODES, buildStopGeomIndices, buildStepGeomIndices, findNextStop, findNextWalkStep, buildSimPoints, detectCurrentLeg, SimPt } from 'src/lib/navigationUtils'
import { getTransitModeLabel } from 'src/lib/directions'

const STORAGE_KEY = 'navPlan'
const EXPIRY_MS = 12 * 60 * 60 * 1000
const ALTERNATIVE_CHECK_INTERVAL_MS = 30_000 // Check for alternatives every 30s (not every tick)

// Mock data for development testing
function getMockArrivals(): Arrival[] {
    const baseTime = Date.now()
    return [
        {
            agency_id: 'MOCK',
            agency_name: 'Mock Agency',
            route_id: 'MOCK:1',
            route_short_name: '1',
            route_color_background: null,
            route_color_text: null,
            trip_id: 'MOCK:1',
            trip_headsign: 'Center',
            realtime: false,
            realtime_status: 'UNKNOWN',
            passed: false,
            arrival_scheduled: baseTime + 5 * 60 * 1000,
            arrival_realtime: null,
            arrival_delay: null,
            departure_scheduled: baseTime + 5 * 60 * 1000,
            departure_realtime: null,
            departure_delay: 0,
            alerts: null,
        },
        {
            agency_id: 'MOCK',
            agency_name: 'Mock Agency',
            route_id: 'MOCK:5',
            route_short_name: '5',
            route_color_background: null,
            route_color_text: null,
            trip_id: 'MOCK:2',
            trip_headsign: 'Airport',
            realtime: false,
            realtime_status: 'UNKNOWN',
            passed: false,
            arrival_scheduled: baseTime + 8 * 60 * 1000,
            arrival_realtime: null,
            arrival_delay: null,
            departure_scheduled: baseTime + 8 * 60 * 1000,
            departure_realtime: null,
            departure_delay: 180,
            alerts: null,
        },
        {
            agency_id: 'MOCK',
            agency_name: 'Mock Agency',
            route_id: 'MOCK:3',
            route_short_name: '3',
            route_color_background: null,
            route_color_text: null,
            trip_id: 'MOCK:3',
            trip_headsign: 'West End',
            realtime: false,
            realtime_status: 'UNKNOWN',
            passed: false,
            arrival_scheduled: baseTime + 15 * 60 * 1000,
            arrival_realtime: null,
            arrival_delay: null,
            departure_scheduled: baseTime + 15 * 60 * 1000,
            departure_realtime: null,
            departure_delay: 0,
            alerts: null,
        },
    ]
}

// Check if we should use mock data (DEV mode or explicit flag)
const USE_MOCK_DATA = typeof window !== 'undefined' && window.location?.hostname === 'localhost'

interface TransferOption {
    tripId: string
    routeName: string
    headsign: string
    departureTime: number
    bufferMs: number
    gtfsId: string
}

interface NavState {
    plan: Plan
    legIndex: number
    userPos: { lat: number, lon: number } | null
    savedAt: number
    delaySeconds?: number
    nextStop?: { name: string, gtfs_id: string } | null
    stopsRemaining?: number
    transferStatus?: 'ok' | 'at-risk' | 'missed'
    timeUntilAlight?: number
    nextWalkStep?: Step | null
    currentTransfer?: {
        stopId: string
        stopName: string
        plannedDepartureTime: number
        plannedBufferMs: number
        alternative?: TransferOption
    }
}

interface NavigatorContextValue {
    navState: NavState | null
    startNavigation: (plan: Plan, startLegIndex?: number) => void
    stopNavigation: () => void
    updateLegIndex: (fn: (prev: number) => number) => void
    updateUserPos: (pos: { lat: number, lon: number }) => void
    isSimulating: boolean
    startSimulation: (speedMultiplier?: number) => void
    stopSimulation: () => void
    switchToAlternative: (alternative: TransferOption) => void
}

export const NavigatorContext = createContext<NavigatorContextValue>({
    navState: null,
    startNavigation: () => {},
    stopNavigation: () => {},
    updateLegIndex: () => {},
    updateUserPos: () => {},
    isSimulating: false,
    startSimulation: () => {},
    stopSimulation: () => {},
    switchToAlternative: () => {},
})

function computeStopsRemaining(leg: { intermediate_stops?: { gtfs_id: string }[] | null }, nextStopGtfsId: string | null | undefined): number | undefined {
    if (!leg.intermediate_stops?.length) return undefined
    if (!nextStopGtfsId) return leg.intermediate_stops.length
    const idx = leg.intermediate_stops.findIndex(s => s.gtfs_id === nextStopGtfsId)
    return idx >= 0 ? leg.intermediate_stops.length - idx : leg.intermediate_stops.length
}

function loadFromStorage(): NavState | null {
    try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
            const parsed = JSON.parse(saved)
            if (Date.now() - parsed.savedAt < EXPIRY_MS) return parsed
            localStorage.removeItem(STORAGE_KEY)
        }
    }
    catch { /* ignore */ }
    return null
}

function saveToStorage(state: NavState): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

export function NavigatorProvider({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation()
    const [navState, setNavState] = useState<NavState | null>(loadFromStorage)
    const [isSimulating, setIsSimulating] = useState(false)
    const stopIndicesRef = useRef<Map<string, number>>(new Map())
    const stepGeomIndicesRef = useRef<Map<number, number>>(new Map())
    const simRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const simPointsRef = useRef<SimPt[]>([])
    const lastAlternativeCheckRef = useRef<number>(0) // Track last alternative check time
    // Ref always holds latest navState for use in intervals without stale closures
    const navStateRef = useRef(navState)
    useEffect(() => {
        navStateRef.current = navState
    }, [navState])

    // Build sim points whenever plan changes (covers storage-restore case too)
    useEffect(() => {
        if (navState?.plan) simPointsRef.current = buildSimPoints(navState.plan)
    }, [navState?.plan])

    // ── Core actions ─────────────────────────────────────────────────────────

    const startNavigation = useCallback((plan: Plan, startLegIndex: number = 0) => {
        const newState: NavState = { plan, legIndex: startLegIndex, userPos: null, savedAt: Date.now() }
        setNavState(newState)
        saveToStorage(newState)
        const leg = plan.legs[startLegIndex]
        if (leg && TRANSIT_MODES.includes(leg.mode)) stopIndicesRef.current = buildStopGeomIndices(leg)
        if (leg && leg.mode === 'WALK') stepGeomIndicesRef.current = buildStepGeomIndices(leg)
        simPointsRef.current = buildSimPoints(plan)
        resetAllNotifications()
        requestNotificationPermission()
    }, [])

    const stopNavigation = useCallback(() => {
        setNavState(null)
        localStorage.removeItem(STORAGE_KEY)
        if (simRef.current) {
            clearInterval(simRef.current)
            simRef.current = null
        }
        setIsSimulating(false)
        resetAllNotifications()
    }, [])

    const updateLegIndex = useCallback((fn: (prev: number) => number) => {
        setNavState((prev) => {
            if (!prev) return prev
            const newLegIndex = fn(prev.legIndex)
            const newState = { ...prev, legIndex: newLegIndex }
            saveToStorage(newState)
            const leg = prev.plan.legs[newLegIndex]
            if (leg && TRANSIT_MODES.includes(leg.mode)) stopIndicesRef.current = buildStopGeomIndices(leg)
            if (leg && leg.mode === 'WALK') stepGeomIndicesRef.current = buildStepGeomIndices(leg)
            return newState
        })
    }, [])

    const updateUserPos = useCallback((pos: { lat: number, lon: number }) => {
        setNavState(prev => prev ? { ...prev, userPos: pos } : prev)
    }, [])

    // ── GPS watch — lives here so it keeps running regardless of active route ─

    useEffect(() => {
        if (!navState?.plan) return
        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const lat = pos.coords.latitude
                const lon = pos.coords.longitude
                setNavState((prev) => {
                    if (!prev) return prev
                    const newLegIndex = Math.max(prev.legIndex, detectCurrentLeg(prev.plan, lat, lon))
                    if (newLegIndex !== prev.legIndex) {
                        const leg = prev.plan.legs[newLegIndex]
                        if (leg && TRANSIT_MODES.includes(leg.mode)) stopIndicesRef.current = buildStopGeomIndices(leg)
                        if (leg && leg.mode === 'WALK') stepGeomIndicesRef.current = buildStepGeomIndices(leg)
                    }
                    const leg = prev.plan.legs[newLegIndex]
                    const nextStopObj = (leg && TRANSIT_MODES.includes(leg.mode))
                        ? findNextStop(leg, lat, lon, stopIndicesRef.current)
                        : null
                    const nextStop = nextStopObj ? { name: nextStopObj.name, gtfs_id: nextStopObj.gtfs_id } : null
                    const stopsRemaining = computeStopsRemaining(leg, nextStop?.gtfs_id)
                    const nextWalkStep = (leg && leg.mode === 'WALK')
                        ? findNextWalkStep(leg, lat, lon, stepGeomIndicesRef.current)
                        : null
                    return { ...prev, userPos: { lat, lon }, legIndex: newLegIndex, nextStop, stopsRemaining, nextWalkStep }
                })
            },
            err => console.warn('[Navigator] geo:', err),
            { enableHighAccuracy: true },
        )
        return () => navigator.geolocation.clearWatch(watchId)
    }, [navState !== null]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Leg-entry notifications — fire once when legIndex advances ────────────

    useEffect(() => {
        if (!navState?.plan) return
        const { plan, legIndex } = navState
        const leg = plan.legs[legIndex]
        if (!leg) return
        const minsUntil = Math.round((leg.from_point.departure_time - Date.now()) / 60000)

        if (TRANSIT_MODES.includes(leg.mode)) {
            // Title is translated
            const title = `${t('Board at')}: ${leg.trip?.short_name ?? t('transit')}${minsUntil > 0 ? ` ${t('in')} ${minsUntil} min` : ''}`
            const body = `${t('from')} ${leg.from_point.name} ${t('to')} ${leg.trip?.headsign ?? leg.to_point.name}`
            notify(
                `board_${legIndex}`,
                title,
                body,
            )
        }
        else if (leg.mode === 'WALK') {
            notify(`walk_${legIndex}`, t('Walk'), `${t('Walk')} ${t('to')} ${leg.to_point.name}`)
        }
        else if (['BICYCLE', 'SCOOTER', 'CAR'].includes(leg.mode)) {
            const verb = leg.mode === 'BICYCLE' ? t('Cycle') : leg.mode === 'SCOOTER' ? t('Ride') : t('Drive')
            notify(`ride_${legIndex}`, verb, `${t('to')} ${leg.to_point.name}`)
        }
    }, [navState?.legIndex, navState?.plan, t]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Alternative checking function ─────────────────────────────────────────

    async function checkAlternativesAtStops(
        leg: Leg,
        _legIndex: number,
        _plan: Plan,
        currentArrivalDelaySecs: number,
        isSim: boolean,
    ): Promise<{ alternative?: TransferOption, isIntermediate?: boolean }> {
        const now = Date.now()

        // Rate limiting: only check every ALTERNATIVE_CHECK_INTERVAL_MS
        if (now - lastAlternativeCheckRef.current < ALTERNATIVE_CHECK_INTERVAL_MS) {
            return {}
        }
        lastAlternativeCheckRef.current = now

        // Determine which stops to check: intermediate stops + final destination
        const stopsToCheck: { gtfs_id: string, name: string, arrivalTime: number }[] = []

        // Add intermediate stops
        if (leg.intermediate_stops) {
            for (const stop of leg.intermediate_stops) {
                if (stop.gtfs_id && stop.arrival_time) {
                    stopsToCheck.push({
                        gtfs_id: stop.gtfs_id,
                        name: stop.name,
                        arrivalTime: stop.arrival_time + currentArrivalDelaySecs * 1000,
                    })
                }
            }
        }

        // Add final destination
        if (leg.to_point.gtfs_id && leg.to_point.arrival_time) {
            stopsToCheck.push({
                gtfs_id: leg.to_point.gtfs_id,
                name: leg.to_point.name,
                arrivalTime: leg.to_point.arrival_time + currentArrivalDelaySecs * 1000,
            })
        }

        // For simulation mode, generate fake alternatives
        if (isSim) {
            console.log('[Alternatives] Simulation mode - generating fake alternatives')
            const firstStop = stopsToCheck[0]
            if (firstStop) {
                const fakeAlternative: TransferOption = {
                    tripId: 'SIM:ALTERNATIVE',
                    routeName: '99',
                    headsign: 'Test Route',
                    departureTime: firstStop.arrivalTime + 5 * 60 * 1000,
                    bufferMs: 5 * 60 * 1000,
                    gtfsId: firstStop.gtfs_id,
                }
                return { alternative: fakeAlternative, isIntermediate: stopsToCheck.indexOf(firstStop) < stopsToCheck.length - 1 }
            }
        }

        // Check each stop for alternatives
        for (const stop of stopsToCheck) {
            let arrivals: Arrival[]

            if (USE_MOCK_DATA) {
                arrivals = getMockArrivals()
            }
            else {
                try {
                    const resp = await listArrivalsStopsStopIdArrivalsGet({
                        path: { stop_id: stop.gtfs_id },
                    })
                    arrivals = resp.data ?? []
                }
                catch {
                    continue
                }
            }

            const ourArrival = stop.arrivalTime

            const betterAlternative = arrivals
                .filter(a => a.trip_id && a.departure_scheduled)
                .map((a) => {
                    const altDepartureTime = (a.departure_scheduled ?? 0) + ((a.departure_delay ?? 0) * 1000)
                    const altBufferMs = altDepartureTime - ourArrival
                    return {
                        tripId: a.trip_id,
                        routeName: a.route_short_name || 'Transit',
                        headsign: a.trip_headsign || '',
                        departureTime: altDepartureTime,
                        bufferMs: altBufferMs,
                        gtfsId: stop.gtfs_id,
                    }
                })
                .filter(a => a.bufferMs > 180_000)
                .sort((a, b) => b.bufferMs - a.bufferMs)[0]

            if (betterAlternative) {
                const isIntermediate = stop.gtfs_id !== leg.to_point.gtfs_id
                console.log('[Alternatives] Found at', stop.name, { isIntermediate, routeName: betterAlternative.routeName })
                return { alternative: betterAlternative, isIntermediate }
            }
        }

        return {}
    }

    // ── 15s polling — reads via ref to avoid restarting on every position update

    useEffect(() => {
        if (!navState?.plan) return

        const tick = async () => {
            const state = navStateRef.current
            if (!state) return
            const { plan, legIndex, userPos } = state
            const leg = plan.legs[legIndex]
            if (!leg) return

            const now = Date.now()
            const isTransit = TRANSIT_MODES.includes(leg.mode)

            let currentArrivalDelayMs = 0
            if (isTransit && leg.to_point.gtfs_id) {
                try {
                    const exitResp = await listArrivalsStopsStopIdArrivalsGet({
                        path: { stop_id: leg.to_point.gtfs_id },
                    })
                    const exitArr = (exitResp.data ?? [])?.find((a: Arrival) => a.trip_id === leg.trip?.gtfs_id)
                    currentArrivalDelayMs = (exitArr?.arrival_delay ?? 0) * 1000
                }
                catch { /* ignore */ }
            }

            if (isTransit) {
                const minsUntil = Math.round((leg.from_point.departure_time - now) / 60000)
                if (minsUntil <= 5 && minsUntil >= 0) {
                    notify(
                        `board_5min_${legIndex}`,
                        `${leg.trip?.short_name} in ${minsUntil} min`,
                        `Board at ${leg.from_point.name}`,
                    )
                }
            }

            let nextStopFromPos: { name: string, gtfs_id: string } | null = null
            if (isTransit && userPos) {
                const next = findNextStop(leg, userPos.lat, userPos.lon, stopIndicesRef.current)
                if (next) nextStopFromPos = { name: next.name, gtfs_id: next.gtfs_id }
            }

            const realtimeArrivalMs = leg.to_point.arrival_time + currentArrivalDelayMs
            const timeUntilAlight = realtimeArrivalMs - now

            if (isTransit) {
                const nextIdx = nextStopFromPos && leg.intermediate_stops
                    ? leg.intermediate_stops.findIndex(s => s.gtfs_id === nextStopFromPos!.gtfs_id)
                    : -1
                const stopsLeft = nextIdx >= 0 ? leg.intermediate_stops!.length - nextIdx : 0

                if (stopsLeft === 1) notify(`alight_soon_${legIndex}`, t('Get off at next stop'), `${t('Alight at')} ${leg.to_point.name}`)

                const twoMinsMs = 2 * 60 * 1000
                if (timeUntilAlight > 0 && timeUntilAlight <= twoMinsMs) {
                    const minsLeft = Math.ceil(timeUntilAlight / 60000)
                    const nextLeg = plan.legs[legIndex + 1]
                    const walkAfter = nextLeg?.mode === 'WALK' ? ` (${Math.ceil((nextLeg.end_time - nextLeg.start_time) / 60000)} min walk after)` : ''
                    notify(`alight_2min_${legIndex}`, t('Get off soon'), `${t('Alight at')} ${leg.to_point.name} ${t('in')} ${minsLeft} min${walkAfter}`)
                }
            }

            // TODO: turn-by-turn announcements for walk legs require tracking which step
            // the user has passed via GPS position, not step.distance (which is segment length).
            // Implement by projecting userPos onto step waypoints in the GPS watch effect.

            // Find next transit leg, accounting for walk time in between
            let nextTransit = null
            let walkTimeMs = 0

            for (let i = legIndex + 1; i < plan.legs.length; i++) {
                const l = plan.legs[i]
                if (l.mode === 'WALK') {
                    walkTimeMs += (l.end_time - l.start_time)
                }
                else if (TRANSIT_MODES.includes(l.mode)) {
                    nextTransit = l
                    break
                }
            }

            console.log('[Transfer] Next leg info:', { nextTransitLegIndex: nextTransit ? plan.legs.indexOf(nextTransit) : null, walkTimeMs, walkTimeMin: Math.round(walkTimeMs / 60000) })

            if (!nextTransit?.trip || !nextTransit.from_point.gtfs_id) {
                setNavState(prev => prev
                    ? {
                            ...prev,
                            delaySeconds: Math.round(currentArrivalDelayMs / 1000),
                            nextStop: nextStopFromPos,
                            stopsRemaining: computeStopsRemaining(leg, nextStopFromPos?.gtfs_id),
                            transferStatus: undefined,
                            timeUntilAlight: timeUntilAlight > 0 ? timeUntilAlight : undefined,
                        }
                    : prev)
                return
            }

            try {
                const currentArrivalDelaySecs = Math.round(currentArrivalDelayMs / 1000)
                let connDepartDelaySecs = 0
                const connResp = await listArrivalsStopsStopIdArrivalsGet({
                    path: { stop_id: nextTransit.from_point.gtfs_id },
                })
                const conn = (connResp.data ?? [])?.find((a: Arrival) => a.trip_id === nextTransit.trip!.gtfs_id)

                console.log('[Transfer] Checking connection:', {
                    stopId: nextTransit.from_point.gtfs_id,
                    lookingForTripId: nextTransit.trip?.gtfs_id,
                    foundTripIds: connResp.data?.map((a: Arrival) => a.trip_id),
                    connFound: !!conn,
                })

                if (!conn) {
                    console.warn('[Transfer] Connection trip not found in API response, using scheduled times only')
                    // Use scheduled times only, treat as at-risk since we don't have real-time data
                    const ourArrivalScheduled = leg.to_point.arrival_time
                    const theirDepartureScheduled = nextTransit.from_point.departure_time
                    const bufferMs = theirDepartureScheduled - ourArrivalScheduled

                    let newTransferStatus: 'ok' | 'at-risk' | 'missed'
                    if (bufferMs < 300_000) { // Less than 5 min buffer without real-time data = at-risk
                        newTransferStatus = 'at-risk'
                        const mode = getTransitModeLabel(nextTransit.mode)
                        notify(
                            `atrisk_${legIndex}_${nextTransit.trip.gtfs_id}_noreltime`,
                            t('Transfer at risk'),
                            t('min_to_catch_no_realtime', { count: Math.round(bufferMs / 60000), mode, trip: nextTransit.trip.short_name }),
                        )
                    }
                    else {
                        newTransferStatus = 'ok'
                    }

                    setNavState(prev => prev
                        ? {
                                ...prev,
                                delaySeconds: currentArrivalDelaySecs,
                                nextStop: nextStopFromPos,
                                stopsRemaining: computeStopsRemaining(leg, nextStopFromPos?.gtfs_id),
                                transferStatus: newTransferStatus,
                                timeUntilAlight: timeUntilAlight > 0 ? timeUntilAlight : undefined,
                            }
                        : prev)
                    return
                }

                connDepartDelaySecs = conn?.departure_delay ?? 0

                const ourArrivalRealtime = leg.to_point.arrival_time + currentArrivalDelaySecs * 1000
                const theirDepartureRealtime = nextTransit.from_point.departure_time + connDepartDelaySecs * 1000
                // Subtract walk time from buffer (we need to walk between arrival and next departure)
                const bufferMs = theirDepartureRealtime - ourArrivalRealtime - walkTimeMs

                console.log('[Transfer] Buffer calculation:', {
                    ourArrival: new Date(ourArrivalRealtime).toISOString(),
                    theirDeparture: new Date(theirDepartureRealtime).toISOString(),
                    walkTimeMs,
                    walkTimeMin: Math.round(walkTimeMs / 60000),
                    bufferMs,
                    bufferMin: Math.round(bufferMs / 60000),
                    currentDelaySecs: currentArrivalDelaySecs,
                    connDelaySecs: connDepartDelaySecs,
                })

                let newTransferStatus: 'ok' | 'at-risk' | 'missed'
                if (bufferMs < 0) {
                    newTransferStatus = 'missed'
                    notify(
                        `missed_${legIndex}_${nextTransit.trip.gtfs_id}`,
                        t('Transfer missed'),
                        `${nextTransit.trip.short_name} ${t('departed')} — ${t('Re-plan')} needed`,
                    )
                }
                else if (bufferMs < 120_000) {
                    newTransferStatus = 'at-risk'
                    const mode = getTransitModeLabel(nextTransit.mode)
                    notify(
                        `atrisk_${legIndex}_${nextTransit.trip.gtfs_id}_${Math.round((currentArrivalDelaySecs || connDepartDelaySecs) / 60)}`,
                        t('Transfer at risk'),
                        t('min_to_catch', { count: Math.round(bufferMs / 60000), mode, trip: nextTransit.trip.short_name }),
                    )
                }
                else {
                    newTransferStatus = 'ok'
                }

                // Find alternative connections that are better than the planned one
                let alternative: TransferOption | undefined

                const allArrivals = connResp.data ?? []
                const plannedDepartureTime = theirDepartureRealtime

                const stopId = nextTransit.from_point.gtfs_id ?? ''

                // Find first alternative that's at least 3 min better
                type AlternativeOption = {
                    tripId: string
                    routeName: string
                    headsign: string
                    departureTime: number
                    bufferMs: number
                    gtfsId: string
                }
                const betterAlternative = allArrivals
                    .filter((a: Arrival) =>
                        a.trip_id !== nextTransit.trip?.gtfs_id
                        && ['BUS', 'RAIL', 'TRAM', 'SUBWAY', 'FERRY'].includes(a.route_id?.split(':')[0] || ''),
                    )
                    .map((a: Arrival): AlternativeOption => {
                        const altDepartureTime = (a.departure_scheduled ?? 0) + ((a.departure_delay ?? 0) * 1000)
                        const altBufferMs = altDepartureTime - ourArrivalRealtime - walkTimeMs
                        return {
                            tripId: a.trip_id,
                            routeName: a.route_short_name || 'Transit',
                            headsign: a.trip_headsign || '',
                            departureTime: altDepartureTime,
                            bufferMs: altBufferMs,
                            gtfsId: stopId,
                        }
                    })
                    .filter((a: AlternativeOption) => a.bufferMs > bufferMs + 180_000) // at least 3 min better
                    .sort((a: AlternativeOption, b: AlternativeOption) => b.bufferMs - a.bufferMs)[0] // best buffer first

                if (betterAlternative) {
                    console.log('[Transfer] Found better alternative:', {
                        routeName: betterAlternative.routeName,
                        headsign: betterAlternative.headsign,
                        improvement: Math.round((betterAlternative.bufferMs - bufferMs) / 60000),
                    })
                    alternative = betterAlternative
                }

                setNavState(prev => prev
                    ? {
                            ...prev,
                            delaySeconds: currentArrivalDelaySecs,
                            nextStop: nextStopFromPos,
                            stopsRemaining: computeStopsRemaining(leg, nextStopFromPos?.gtfs_id),
                            transferStatus: newTransferStatus,
                            timeUntilAlight: timeUntilAlight > 0 ? timeUntilAlight : undefined,
                            currentTransfer: nextTransit.from_point.gtfs_id
                                ? {
                                        stopId: nextTransit.from_point.gtfs_id,
                                        stopName: nextTransit.from_point.name,
                                        plannedDepartureTime,
                                        plannedBufferMs: bufferMs,
                                        alternative,
                                    }
                                : undefined,
                        }
                    : prev)
            }
            catch (e) {
                console.error('[Transfer] Error checking connection:', e)
                setNavState(prev => prev
                    ? {
                            ...prev,
                            delaySeconds: Math.round(currentArrivalDelayMs / 1000),
                            nextStop: nextStopFromPos,
                            stopsRemaining: computeStopsRemaining(leg, nextStopFromPos?.gtfs_id),
                            timeUntilAlight: timeUntilAlight > 0 ? timeUntilAlight : undefined,
                        }
                    : prev)
            }

            // Also check for alternatives at intermediate stops (not just final transfer point)
            if (isTransit) {
                try {
                    const altResult = await checkAlternativesAtStops(leg, legIndex, plan, Math.round(currentArrivalDelayMs / 1000), isSimulating)
                    if (altResult.alternative) {
                        const alt = altResult.alternative
                        setNavState(prev => prev
                            ? ({
                                    ...prev,
                                    currentTransfer: prev.currentTransfer
                                        ? {
                                                ...prev.currentTransfer,
                                                alternative: alt,
                                            }
                                        : {
                                                stopId: alt.gtfsId,
                                                stopName: 'Intermediate stop',
                                                plannedDepartureTime: Date.now(),
                                                plannedBufferMs: 0,
                                                alternative: alt,
                                            },
                                })
                            : prev)
                    }
                }
                catch (e) {
                    console.error('[Alternatives] Error checking intermediate stops:', e)
                }
            }
        }

        tick()
        const interval = setInterval(tick, 15_000)
        return () => clearInterval(interval)
    }, [navState?.plan, navState?.legIndex, t, isSimulating])

    // ── Simulation ───────────────────────────────────────────────────────────

    const startSimulation = useCallback((speedMultiplier: number = 10) => {
        if (simRef.current) clearInterval(simRef.current)

        // Rebuild sim points if missing (e.g. restored from storage)
        if (!simPointsRef.current.length) {
            const plan = navStateRef.current?.plan
            if (!plan) {
                console.warn('[sim] no plan')
                return
            }
            simPointsRef.current = buildSimPoints(plan)
        }
        if (!simPointsRef.current.length) {
            console.warn('[sim] no geometry')
            return
        }

        let i = 0
        const delay = 200 / speedMultiplier
        console.log(`[simulateNavigation] ${simPointsRef.current.length} pts · ${speedMultiplier}x · ${delay.toFixed(0)}ms/step`)

        setIsSimulating(true)
        simRef.current = setInterval(() => {
            if (i >= simPointsRef.current.length) {
                if (simRef.current) {
                    clearInterval(simRef.current)
                    simRef.current = null
                }
                setIsSimulating(false)
                console.log('[simulateNavigation] done')
                return
            }
            const { lat, lon, legIdx } = simPointsRef.current[i++]
            setNavState((prev) => {
                if (!prev) return prev
                const newLegIndex = Math.max(prev.legIndex, legIdx)
                if (newLegIndex !== prev.legIndex) {
                    const leg = prev.plan.legs[newLegIndex]
                    if (leg && TRANSIT_MODES.includes(leg.mode)) stopIndicesRef.current = buildStopGeomIndices(leg)
                    if (leg && leg.mode === 'WALK') stepGeomIndicesRef.current = buildStepGeomIndices(leg)
                }
                const leg = prev.plan.legs[newLegIndex]
                const nextStopObj = (leg && TRANSIT_MODES.includes(leg.mode))
                    ? findNextStop(leg, lat, lon, stopIndicesRef.current)
                    : null
                const nextStop = nextStopObj ? { name: nextStopObj.name, gtfs_id: nextStopObj.gtfs_id } : null
                const stopsRemaining = computeStopsRemaining(leg, nextStop?.gtfs_id)
                const nextWalkStep = (leg && leg.mode === 'WALK')
                    ? findNextWalkStep(leg, lat, lon, stepGeomIndicesRef.current)
                    : null
                return { ...prev, userPos: { lat, lon }, legIndex: newLegIndex, nextStop, stopsRemaining, nextWalkStep }
            })
        }, delay)
    }, [])

    const stopSimulation = useCallback(() => {
        if (simRef.current) {
            clearInterval(simRef.current)
            simRef.current = null
        }
        setIsSimulating(false)
        console.log('[simulateNavigation] stopped')
    }, [])

    const switchToAlternative = useCallback((alternative: TransferOption) => {
        if (!navState?.plan) return

        // Don't allow switching to simulated alternatives (for demo purposes)
        if (alternative.tripId.startsWith('SIM:')) {
            console.log('[switchToAlternative] Simulation mode - not actually switching (demo only)')
            // Clear the alternative so the UI doesn't keep showing it
            setNavState(prev => prev
                ? {
                        ...prev,
                        currentTransfer: undefined,
                    }
                : prev)
            return
        }

        // Find the leg index of the alternative in the plan
        const newLegIndex = navState.plan.legs.findIndex(l =>
            l.trip?.gtfs_id === alternative.tripId
            && l.from_point.gtfs_id === navState.currentTransfer?.stopId,
        )

        if (newLegIndex >= 0) {
            console.log('[switchToAlternative] Switching to leg index:', newLegIndex, alternative)
            setNavState(prev => prev
                ? {
                        ...prev,
                        legIndex: newLegIndex,
                        currentTransfer: undefined,
                    }
                : prev)
        }
        else {
            console.warn('[switchToAlternative] Could not find leg for alternative:', alternative.tripId)
        }
    }, [navState])

    // ── Console debug helpers ─────────────────────────────────────────────────

    useEffect(() => {
        const windowExt = window as unknown as Record<string, unknown>
        windowExt.simulateNavigation = (speed = 10) => startSimulation(speed)
        windowExt.stopSimulation = stopSimulation
        return () => {
            delete windowExt.simulateNavigation
            delete windowExt.stopSimulation
        }
    }, [startSimulation, stopSimulation])

    const value = useMemo(
        () => ({ navState, startNavigation, stopNavigation, updateLegIndex, updateUserPos, isSimulating, startSimulation, stopSimulation, switchToAlternative }),
        [navState, startNavigation, stopNavigation, updateLegIndex, updateUserPos, isSimulating, startSimulation, stopSimulation, switchToAlternative],
    )

    return (
        <NavigatorContext.Provider value={value}>
            {children}
        </NavigatorContext.Provider>
    )
}

export function useNavigatorContext() {
    return useContext(NavigatorContext)
}
